import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ShieldAlert, Flag } from "lucide-react";
import { useApp } from "@/context/AppContext";

// Reusable report dialog with an explicit, prioritized CSAE (child-safety) option
// followed by the standard reasons. onReport(reason, category) is called on choice.
export const ReportDialog = ({ open, onOpenChange, onReport, reporting = false, testidPrefix = "report", title, subtitle }) => {
  const { t, config } = useApp();
  const showChild = config?.settings?.csae?.report_option_enabled !== false;

  const reasons = [
    { key: "spam", label: t("report.reason_spam", "Spam ou arnaque") },
    { key: "hate", label: t("report.reason_hate", "Harcèlement ou discours haineux") },
    { key: "sexual", label: t("report.reason_sexual", "Nudité ou contenu sexuel") },
    { key: "violence", label: t("report.reason_violence", "Violence ou contenu choquant") },
    { key: "misinfo", label: t("report.reason_misinfo", "Fausse information") },
    { key: "other", label: t("report.reason_other", "Autre contenu inapproprié") },
  ];

  const childLabel = t("report.reason_child", "Mise en danger d'un mineur / sécurité des enfants");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md z-[95]" data-testid={`${testidPrefix}-dialog`}>
        <DialogHeader>
          <DialogTitle>{title || t("report.title", "Signaler")}</DialogTitle>
          <DialogDescription>{subtitle || t("report.subtitle", "Choisis un motif. Notre équipe de modération examinera ce signalement.")}</DialogDescription>
        </DialogHeader>

        <button
          type="button"
          disabled={reporting}
          onClick={() => onReport(childLabel, "child_safety")}
          className="w-full flex items-start gap-3 rounded-xl border-2 p-3 text-left transition-colors hover:bg-red-50 disabled:opacity-50"
          style={{ borderColor: "#DC2626", display: showChild ? undefined : "none" }}
          data-testid={`${testidPrefix}-child-safety`}
        >
          <ShieldAlert size={20} className="shrink-0 mt-0.5 text-red-600" />
          <span>
            <span className="block text-sm font-semibold text-red-700">{childLabel}</span>
            <span className="block text-xs text-red-600/80">{t("report.reason_child_hint", "Contenu impliquant l'exploitation ou la mise en danger d'un enfant. Traité en priorité absolue.")}</span>
          </span>
        </button>

        <div className="space-y-1.5">
          {reasons.map((r) => (
            <button
              key={r.key}
              type="button"
              disabled={reporting}
              onClick={() => onReport(r.label, r.key)}
              className="w-full flex items-center gap-2 rounded-xl border p-2.5 text-left text-sm transition-colors hover:bg-[var(--yalola-surface-alt)] disabled:opacity-50"
              style={{ borderColor: "var(--yalola-border)" }}
              data-testid={`${testidPrefix}-reason-${r.key}`}
            >
              <Flag size={15} className="shrink-0" style={{ color: "var(--yalola-text-muted)" }} />
              {r.label}
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};
